/**
 * Central API module — all backend calls in one place.
 * Every function returns the parsed JSON response.
 *
 * Usage:
 *   import { getAllProducts, placeOrder, getDashboardStats } from "../../common/api/api";
 */

import { backend_url } from "../../App";

// ── Internal helpers ──────────────────────────────────────────────────────────

const getToken = () => localStorage.getItem("auth-token");

const _get = async (endpoint, withAuth = false) => {
  const res = await fetch(`${backend_url}${endpoint}`, {
    headers: withAuth ? { "auth-token": getToken() } : {},
  });
  return res.json();
};

const _post = async (endpoint, body = {}, withAuth = false) => {
  const res = await fetch(`${backend_url}${endpoint}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(withAuth ? { "auth-token": getToken() } : {}),
    },
    body: JSON.stringify(body),
  });
  return res.json();
};

// ── Auth ──────────────────────────────────────────────────────────────────────

export const login = (email, password) =>
  _post("/login", { email, password });

export const signup = (name, email, password) =>
  _post("/signup", { name, email, password });

export const getUser = () => _get("/getuser", true);

// ── Products ──────────────────────────────────────────────────────────────────

export const getAllProducts = () => _get("/allproducts");
export const getNewCollections = () => _get("/newcollections");
export const getPopularInWomen = () => _get("/popularinwomen");
export const getRelatedProducts = (category) =>
  _post("/relatedproducts", { category });

export const addProduct = (data) => _post("/addproduct", data, true);
export const removeProduct = (id) => _post("/removeproduct", { id }, true);
export const updateProduct = (data) => _post("/updateproduct", data, true);

/** Image upload — multipart, requires admin auth */
export const uploadImage = (formData) =>
  fetch(`${backend_url}/upload`, {
    method: "POST",
    headers: { "auth-token": getToken() },
    body: formData,
  }).then((r) => r.json());

/** Multiple image upload — multipart, public endpoint */
export const uploadMultiple = (formData) =>
  fetch(`${backend_url}/uploadmultiple`, {
    method: "POST",
    body: formData,
  }).then((r) => r.json());

// ── Cart ──────────────────────────────────────────────────────────────────────

export const getCart = () => _post("/getcart", {}, true);
export const addToCart = (itemId) => _post("/addtocart", { itemId }, true);
export const removeFromCart = (itemId) =>
  _post("/removefromcart", { itemId }, true);

// ── Orders ────────────────────────────────────────────────────────────────────

export const getAllOrders = () => _get("/allorders", true);
export const getMyOrders = () => _get("/myorders", true);
export const placeOrder = (data) => _post("/placeorder", data, true);
export const updateOrderStatus = (data) =>
  _post("/updateorderstatus", data, true);
export const updatePaymentStatus = (data) =>
  _post("/updatepaymentstatus", data, true);
export const deleteOrder = (data) => _post("/deleteorder", data, true);

// ── Reviews ───────────────────────────────────────────────────────────────────

export const getAllReviews = () => _get("/allreviews", true);
export const getProductReviews = (productId) =>
  _get(`/productreviews/${productId}`);
export const addReview = (data) => _post("/addreview", data, true);
export const approveReview = (data) => _post("/approvereview", data, true);
export const deleteReview = (data) => _post("/deletereview", data, true);

// ── Coupons ───────────────────────────────────────────────────────────────────

export const getAllCoupons = () => _get("/allcoupons", true);
export const addCoupon = (data) => _post("/addcoupon", data, true);
export const validateCoupon = (data) => _post("/validatecoupon", data); // public
export const toggleCoupon = (data) => _post("/togglecoupon", data, true);
export const deleteCoupon = (data) => _post("/deletecoupon", data, true);

// ── Users ─────────────────────────────────────────────────────────────────────

export const getAllUsers = () => _get("/allusers", true);
export const deleteUser = (data) => _post("/deleteuser", data, true);
export const updateUserRole = (data) => _post("/updateuserrole", data, true);
export const updateProfile = (data) => _post("/updateprofile", data, true);

// ── Settings ──────────────────────────────────────────────────────────────────

export const getSettings = () => _get("/getsettings");
export const updateSetting = (data) => _post("/updatesetting", data, true);

// ── Profile Picture ───────────────────────────────────────────────────────────

/** Upload profile picture — multipart/form-data, no auth required */
export const uploadProfilePicture = (formData) =>
  fetch(`${backend_url}/uploadprofile`, { method: "POST", body: formData }).then((r) => r.json());

/** Delete profile picture from Cloudinary by publicId */
export const deleteProfilePicture = (publicId) =>
  _post("/deleteprofile", { publicId });

// ── Dashboard ─────────────────────────────────────────────────────────────────

export const getDashboardStats = () => _get("/dashboardstats", true);

// ── Categories ────────────────────────────────────────────────────────────────

export const getCategories = () => _get("/categories");
export const addCategory = (data) => _post("/addcategory", data, true);
export const updateCategory = (id, data) => _post("/updatecategory", { id, data }, true);
export const deleteCategory = (id) => _post("/deletecategory", { id }, true);
export const addSubcategory = (categoryId, subcategory) => _post("/addsubcategory", { categoryId, subcategory }, true);
export const updateSubcategory = (categoryId, subcategoryId, data) =>
  _post("/updatesubcategory", { categoryId, subcategoryId, data }, true);
export const deleteSubcategory = (categoryId, subcategoryId) =>
  _post("/deletesubcategory", { categoryId, subcategoryId }, true);

// ── Payment Settings ─────────────────────────────────────────────────────────

export const getPaymentSettings = () => _get("/getsettings");
export const updatePaymentSetting = (key, value) => _post("/updatesetting", { key, value }, true);

// ── Home Content ─────────────────────────────────────────────────────────────

export const getHomeContent = () => _get("/homecontent");
export const updateHomeContent = (section, data) => _post("/updatehomecontent", { section, data }, true);

// ── Backward-compat helpers ───────────────────────────────────────────────────
// Legacy adminGet / adminPost — used by admin pages that haven't migrated yet.

export const adminGet = (endpoint) => _get(endpoint, true);
export const adminPost = (endpoint, body) => _post(endpoint, body, true);
